import BaseFeature from '../../foundation/base/base';

class Tab extends BaseFeature {
	constructor(element) {
		super(element);

		this.elem = element;
		this.generateArrays = this.generateArrays.bind(this);
		this.clickEventListener = this.clickEventListener.bind(this);
		this.activateTab = this.activateTab.bind(this);
		this.keydownEventListener = this.keydownEventListener.bind(this);
		this.keyupEventListener = this.keyupEventListener.bind(this);
		this.determineOrientation = this.determineOrientation.bind(this);
		this.switchTabOnArrowPress = this.switchTabOnArrowPress.bind(this);
		this.focusFirstTab = this.focusFirstTab.bind(this);
		this.focusLastTab = this.focusLastTab.bind(this);
		this.activateTab = this.activateTab.bind(this);

		this.initDomCache();

		this.tablist = this.dom.tablist;

		this.generateArrays();
		// For easy reference
		this.keys = {
			end: 35,
			home: 36,
			left: 37,
			up: 38,
			right: 39,
			down: 40,
			enter: 13,
			space: 32
		};

		// Add or subtract depending on key pressed
		this.direction = {
			37: -1,
			38: -1,
			39: 1,
			40: 1
		};
		// Bind listeners
		for (let i = 0; i < this.tabs.length; ++i) {
			this.addListeners(i);
		}
	}

	generateArrays() {
		this.tabs = this.elem.querySelectorAll('[role="tab"]');
		this.panels = this.elem.querySelectorAll('[role="tabpanel"]');
	}

	addListeners(index) {
		this.tabs[index].addEventListener('click', this.clickEventListener);
		this.tabs[index].addEventListener('keydown', this.keydownEventListener);
		this.tabs[index].addEventListener('keyup', this.keyupEventListener);

		// Build an array with all tabs (<button>s) in it
		this.tabs[index].index = index;
	}

	clickEventListener(event) {
		const tab = event.target;
		this.activateTab(tab, false);
	}

	keydownEventListener(event) {
		const key = event.keyCode;

		switch (key) {
		case this.keys.end:
			event.preventDefault();
			// Activate last tab
			this.focusLastTab();
			break;
		case this.keys.home:
			event.preventDefault();
			// Activate first tab
			this.focusFirstTab();
			break;

			// Up and down are in keydown
			// because we need to prevent page scroll >:)
		case this.keys.up:
		case this.keys.down:
			this.determineOrientation(event);
			break;
		// no default
		}
	}

	keyupEventListener(event) {
		const key = event.keyCode;

		switch (key) {
		case this.keys.left:
		case this.keys.right:
			this.determineOrientation(event);
			break;
		case this.keys.enter:
		case this.keys.space:
			this.activateTab(event.target);
			break;
			// no default
		}
	}

	determineOrientation(event) {
		const key = event.keyCode;
		const vertical = this.tablist.getAttribute('aria-orientation') === 'vertical';
		let proceed = false;

		if (vertical) {
			if (key === this.keys.up || key === this.keys.down) {
				event.preventDefault();
				proceed = true;
			}
		} else if (key === this.keys.left || key === this.keys.right) {
			proceed = true;
		}

		if (proceed) {
			this.switchTabOnArrowPress(event);
		}
	}

	switchTabOnArrowPress(event) {
		const pressed = event.keyCode;
		if (this.direction[pressed]) {
			const { target } = event;
			if (target.index !== undefined) {
				if (this.tabs[target.index + this.direction[pressed]]) {
					this.tabs[target.index + this.direction[pressed]].focus();
				} else if (pressed === this.keys.left || pressed === this.keys.up) {
					this.focusLastTab();
				} else if (pressed === this.keys.right || pressed === this.keys.down) {
					this.focusFirstTab();
				}
			}
		}
	}

	activateTab(tab, setFocus) {
		const setFocusIsTrue = setFocus || true;
		// Deactivate all other tabs
		this.deactivateTabs();

		// Remove tabindex attribute
		tab.removeAttribute('tabindex');

		// Set the tab as selected
		tab.setAttribute('aria-selected', 'true');

		// Get the value of aria-controls (which is an ID)
		const controls = tab.getAttribute('aria-controls');

		// Remove hidden attribute from tab panel to make it visible
		const tabPanelParent = this.panels[0].parentNode;
		tabPanelParent.querySelector('#' + controls).removeAttribute('hidden');

		// Set focus when required
		if (setFocusIsTrue) {
			tab.focus();
		}
	}

	deactivateTabs() {
		for (let t = 0; t < this.tabs.length; t++) {
			this.tabs[t].setAttribute('tabindex', '-1');
			this.tabs[t].setAttribute('aria-selected', 'false');
		}

		for (let p = 0; p < this.panels.length; p++) {
			this.panels[p].setAttribute('hidden', 'hidden');
		}
	}

	focusFirstTab() {
		this.tabs[0].focus();
	}

	focusLastTab() {
		this.tabs[this.tabs.length - 1].focus();
	}
}

export default Tab;
